import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { PlusIcon, PrinterIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { DeliveryChallan, DeliveryChallanLine } from '../../services/warehouse.service';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';

type PrintedDocument = 'challan' | 'packing-list' | 'gate-pass';

const documentsApi = {
  savePacking: async (id: string, packages: object[]): Promise<DeliveryChallan> => (await api.put(`/dispatches/${id}/packing`, { packages })).data,
  gatePass: async (id: string, action: '' | '/out' | '/cancel', data: object = {}): Promise<DeliveryChallan> => (await api.post(`/dispatches/${id}/gate-pass${action}`, data)).data,
  // The PDF is fetched with the sign-in and handed to the browser as a download
  download: async (id: string, document_: PrintedDocument, fallbackName: string): Promise<void> => {
    const response = await api.get(`/dispatches/${id}/print/${document_}`, { responseType: 'blob' });
    const named = /filename="?([^";]+)"?/.exec(String(response.headers['content-disposition'] ?? ''));
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = named?.[1] ?? fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

// A failed download comes back as a blob; its message is inside
const blobMessage = async (error: unknown, fallback: string): Promise<string> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return getApiErrorMessage(error, fallback);
};

const formatMoment = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
const round4 = (value: number) => Math.round(value * 10000) / 10000;
const sectionTitle = 'text-xs font-semibold uppercase tracking-wide text-gray-500';
const PACKAGE_KINDS = ['Box', 'Carton', 'Pallet', 'Bundle', 'Crate', 'Bag', 'Drum'];

interface PackageState {
  kind: string;
  netWeightKg: string;
  grossWeightKg: string;
  dimensions: string;
  // challan line id -> quantity in this package
  quantities: Record<string, string>;
}
const emptyPackage = (): PackageState => ({ kind: 'Box', netWeightKg: '', grossWeightKg: '', dimensions: '', quantities: {} });

// ---------------------------------------------------------------- which package holds what

const PackingEditor: React.FC<{ challan: DeliveryChallan; lines: DeliveryChallanLine[]; onDone: () => void; onSaved: (challan: DeliveryChallan) => void }> = ({ challan, lines, onDone, onSaved }) => {
  const [packages, setPackages] = useState<PackageState[]>(() =>
    (challan.packing ?? []).length > 0
      ? (challan.packing ?? []).map((pack) => ({
          kind: pack.kind,
          netWeightKg: pack.netWeightKg === null ? '' : String(pack.netWeightKg),
          grossWeightKg: pack.grossWeightKg === null ? '' : String(pack.grossWeightKg),
          dimensions: pack.dimensions ?? '',
          quantities: Object.fromEntries(pack.items.map((item) => [item.deliveryChallanItemId, String(item.quantity)])),
        }))
      : [emptyPackage()],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (input: object[]) => documentsApi.savePacking(challan.id, input),
    onSuccess: (saved) => {
      toast.success(saved.packing?.length ? `Packing saved: ${saved.packing.length} package(s)` : 'Packing removed');
      onSaved(saved);
      onDone();
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'The packing could not be saved'), { duration: 7000 }),
  });

  const setPackage = (index: number, patch: Partial<PackageState>) => {
    setPackages((prev) => prev.map((pack, position) => (position === index ? { ...pack, ...patch } : pack)));
    setErrors({});
  };
  const packedOf = (lineId: string) => round4(packages.reduce((sum, pack) => sum + (Number(pack.quantities[lineId]) || 0), 0));
  // Everything not yet in a package goes into this one
  const fillRest = (index: number) =>
    setPackage(index, {
      quantities: Object.fromEntries(
        lines
          .map((line) => [line.id, round4(line.quantity - packedOf(line.id) + (Number(packages[index].quantities[line.id]) || 0))] as const)
          .filter(([, quantity]) => quantity > 0)
          .map(([id, quantity]) => [id, String(quantity)]),
      ),
    });

  const handleSave = () => {
    const next: Record<string, string> = {};
    const output: object[] = [];
    packages.forEach((pack, index) => {
      const key = `package-${index}`;
      const net = pack.netWeightKg.trim() === '' ? null : Number(pack.netWeightKg);
      const gross = pack.grossWeightKg.trim() === '' ? null : Number(pack.grossWeightKg);
      const items = lines
        .filter((line) => (pack.quantities[line.id] ?? '').trim() !== '' && Number(pack.quantities[line.id]) !== 0)
        .map((line) => ({ deliveryChallanItemId: line.id, quantity: Number(pack.quantities[line.id]) }));
      if (!pack.kind.trim()) next[key] = 'Say what kind of package it is';
      else if (items.some((item) => !isQuantity(item.quantity) || item.quantity < 0)) next[key] = 'Use quantities with up to 4 decimals';
      else if (items.length === 0) next[key] = 'Enter what is in this package, or remove it';
      else if ((net !== null && (!Number.isFinite(net) || net < 0)) || (gross !== null && (!Number.isFinite(gross) || gross < 0))) next[key] = 'Enter the weights in kg';
      else if (net !== null && gross !== null && gross < net) next[key] = 'The gross weight cannot be less than the net weight';
      else output.push({ kind: pack.kind.trim(), netWeightKg: net, grossWeightKg: gross, dimensions: pack.dimensions.trim() || null, items });
    });
    const over = lines.find((line) => packedOf(line.id) > line.quantity);
    if (over) next.lines = `${over.productName}: ${formatNumber(packedOf(over.id))} is packed, but only ${formatNumber(over.quantity)} is on the challan`;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate(output);
  };

  return (
    <div className="space-y-3">
      {packages.map((pack, index) => (
        <div key={index} className="rounded-lg border border-gray-200">
          <div className="flex flex-wrap items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-sm font-semibold text-gray-900">Package {index + 1}</span>
            <input list="package-kinds" value={pack.kind} onChange={(event) => setPackage(index, { kind: event.target.value })} maxLength={40} aria-label={`Kind of package ${index + 1}`} placeholder="Kind" className={cn(inputClass, 'w-28 py-1')} />
            <input type="number" min="0" step="0.001" value={pack.netWeightKg} onChange={(event) => setPackage(index, { netWeightKg: event.target.value })} aria-label={`Net weight of package ${index + 1}`} placeholder="Net kg" className={cn(inputClass, 'w-24 py-1 text-right tabular-nums')} />
            <input type="number" min="0" step="0.001" value={pack.grossWeightKg} onChange={(event) => setPackage(index, { grossWeightKg: event.target.value })} aria-label={`Gross weight of package ${index + 1}`} placeholder="Gross kg" className={cn(inputClass, 'w-24 py-1 text-right tabular-nums')} />
            <input type="text" maxLength={60} value={pack.dimensions} onChange={(event) => setPackage(index, { dimensions: event.target.value })} aria-label={`Size of package ${index + 1}`} placeholder="Size, e.g. 600 x 400 x 300 mm" className={cn(inputClass, 'min-w-0 flex-1 py-1')} />
            <button type="button" onClick={() => fillRest(index)} className="text-xs font-semibold text-indigo-600 hover:text-indigo-500" aria-label={`Put the rest in package ${index + 1}`}>
              Put the rest here
            </button>
            <button type="button" onClick={() => { setPackages((prev) => prev.filter((_, position) => position !== index)); setErrors({}); }} aria-label={`Remove package ${index + 1}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
          <ul className="divide-y divide-gray-100">
            {lines.map((line) => (
              <li key={line.id} className="flex items-center gap-3 px-3 py-1.5">
                <p className="min-w-0 flex-1 truncate text-sm text-gray-900">
                  {line.productName} <span className="text-xs text-gray-500">{line.sku}{line.batchNumber ? ` · batch ${line.batchNumber}` : ''}</span>
                </p>
                <input
                  type="number"
                  min="0"
                  step={QUANTITY_STEP}
                  value={pack.quantities[line.id] ?? ''}
                  onChange={(event) => setPackage(index, { quantities: { ...pack.quantities, [line.id]: event.target.value } })}
                  aria-label={`${line.productName} in package ${index + 1}`}
                  placeholder="0"
                  className={cn(inputClass, 'w-28 py-1 text-right tabular-nums')}
                />
                <span className="w-10 text-xs text-gray-500">{line.unit}</span>
              </li>
            ))}
          </ul>
          {errors[`package-${index}`] && <p className="border-t border-gray-100 px-3 py-1.5 text-sm text-red-600">{errors[`package-${index}`]}</p>}
        </div>
      ))}
      <datalist id="package-kinds">
        {PACKAGE_KINDS.map((kind) => (<option key={kind} value={kind} />))}
      </datalist>

      <p className="text-xs text-gray-500">
        {lines.map((line) => {
          const packed = packedOf(line.id);
          return (
            <span key={line.id} className={cn('mr-4 inline-block', packed > line.quantity ? 'font-medium text-red-600' : packed < line.quantity ? 'text-amber-700' : 'text-gray-500')}>
              {line.productName}: {formatNumber(packed)} of {formatNumber(line.quantity)} {line.unit} packed
            </span>
          );
        })}
      </p>
      {errors.lines && <p className="text-sm text-red-600">{errors.lines}</p>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => setPackages((prev) => [...prev, emptyPackage()])} className={secondaryButtonClass}>
          <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
          Add Package
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onDone} disabled={save.isPending} className={secondaryButtonClass}>
            Back
          </button>
          <button type="button" onClick={handleSave} disabled={save.isPending} className={primaryButtonClass}>
            {save.isPending ? 'Saving…' : 'Save Packing'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- packing, gate pass and printing of one challan

export const DispatchDocuments: React.FC<{ challan: DeliveryChallan; canEdit: boolean }> = ({ challan, canEdit }) => {
  const queryClient = useQueryClient();
  const [packing, setPacking] = useState(false);
  const [gateForm, setGateForm] = useState<{ vehicleNumber: string; driverName: string; driverPhone: string; notes: string } | null>(null);
  const [gateError, setGateError] = useState('');
  const [cancelReason, setCancelReason] = useState<string | null>(null);
  const [printing, setPrinting] = useState<PrintedDocument | null>(null);

  const lines = challan.items ?? [];
  const packages = challan.packing ?? [];
  const pass = challan.gatePass ?? null;
  const posted = challan.status === 'posted';
  const mayEdit = posted && canEdit;

  const store = (saved: DeliveryChallan) => {
    queryClient.setQueryData(['dispatches', 'one', challan.id], saved);
    queryClient.invalidateQueries({ queryKey: ['dispatches', 'page'] });
  };
  const gate = useMutation({
    mutationFn: ({ action, data }: { action: '' | '/out' | '/cancel'; data?: object }) => documentsApi.gatePass(challan.id, action, data),
    onSuccess: (saved, { action }) => {
      toast.success(action === '' ? `Gate pass ${saved.gatePass?.gatePassNumber ?? ''} issued` : action === '/out' ? `${saved.gatePass?.gatePassNumber ?? 'The gate pass'} marked as out` : 'Gate pass cancelled');
      store(saved);
      setGateForm(null);
      setCancelReason(null);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'That could not be done'), { duration: 7000 }),
  });

  const print = async (document_: PrintedDocument) => {
    setPrinting(document_);
    try {
      const name = document_ === 'gate-pass' && pass ? pass.gatePassNumber : `${challan.challanNumber}${document_ === 'packing-list' ? '-packing-list' : ''}`;
      await documentsApi.download(challan.id, document_, `${name.replace(/[\\/\s]+/g, '-')}.pdf`);
    } catch (error) {
      toast.error(await blobMessage(error, 'The PDF could not be made'), { duration: 8000 });
    } finally {
      setPrinting(null);
    }
  };
  const printButton = (document_: PrintedDocument, label: string, disabled = false, title?: string) => (
    <button type="button" onClick={() => print(document_)} disabled={disabled || printing !== null} title={title} className={secondaryButtonClass}>
      <PrinterIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
      {printing === document_ ? 'Preparing…' : label}
    </button>
  );

  const issue = () => {
    if (!gateForm) return;
    if (!gateForm.vehicleNumber.trim()) return setGateError('Enter the vehicle number');
    if (gateForm.driverPhone.trim() && !/^[0-9+\- ]{7,20}$/.test(gateForm.driverPhone.trim())) return setGateError('Enter a valid phone number for the driver');
    setGateError('');
    gate.mutate({ action: '', data: { vehicleNumber: gateForm.vehicleNumber.trim(), driverName: gateForm.driverName.trim() || null, driverPhone: gateForm.driverPhone.trim() || null, notes: gateForm.notes.trim() || null } });
  };

  return (
    <div className="space-y-4">
      {/* ---- packing */}
      <section className="rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className={sectionTitle}>Packing{packages.length > 0 ? ` · ${packages.length} package(s)` : ''}</h4>
          {mayEdit && !packing && pass?.status !== 'out' && (
            <button type="button" onClick={() => setPacking(true)} className="text-sm font-semibold text-indigo-600 hover:text-indigo-500">
              {packages.length > 0 ? 'Change Packing' : 'Enter Packing'}
            </button>
          )}
        </div>
        <div className="mt-3">
          {packing ? (
            <PackingEditor challan={challan} lines={lines} onDone={() => setPacking(false)} onSaved={store} />
          ) : packages.length === 0 ? (
            <p className="text-sm text-gray-500">No packing entered. The packing list then prints the lines of the challan{challan.packages != null ? ` and ${formatNumber(challan.packages)} package(s)` : ''}.</p>
          ) : (
            <ul className="divide-y divide-gray-100 text-sm">
              {packages.map((pack) => (
                <li key={pack.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 py-1.5">
                  <span className="font-medium text-gray-900">{pack.packageNumber}. {pack.kind}</span>
                  <span className="min-w-0 flex-1 text-gray-700">{pack.items.map((item) => `${item.productName} × ${formatNumber(item.quantity)} ${item.unit}`).join(', ')}</span>
                  <span className="text-xs text-gray-500 tabular-nums">
                    {[pack.dimensions, pack.netWeightKg !== null ? `net ${formatNumber(pack.netWeightKg, 3)} kg` : '', pack.grossWeightKg !== null ? `gross ${formatNumber(pack.grossWeightKg, 3)} kg` : ''].filter(Boolean).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ---- gate pass */}
      <section className="rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className={sectionTitle}>Gate pass</h4>
          {mayEdit && !pass && !gateForm && (
            <button type="button" onClick={() => { setGateError(''); setGateForm({ vehicleNumber: challan.vehicleNumber ?? '', driverName: '', driverPhone: '', notes: '' }); }} className="text-sm font-semibold text-indigo-600 hover:text-indigo-500">
              Issue Gate Pass
            </button>
          )}
        </div>
        <div className="mt-3">
          {pass ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
                <span className="font-semibold text-gray-900">{pass.gatePassNumber}</span>
                <span className={cn(badgeClass, pass.status === 'out' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-amber-50 text-amber-800 ring-amber-600/20')}>{pass.status === 'out' ? 'Out of the gate' : 'Issued, not yet out'}</span>
                <span>Vehicle {pass.vehicleNumber}</span>
                {pass.driverName && <span>Driver {pass.driverName}{pass.driverPhone ? `, ${pass.driverPhone}` : ''}</span>}
                <span className="text-gray-500">Issued {formatMoment(pass.createdAt)}{pass.issuedBy ? ` by ${pass.issuedBy}` : ''}</span>
                {pass.outAt && <span className="text-gray-500">Out {formatMoment(pass.outAt)}{pass.outBy ? `, recorded by ${pass.outBy}` : ''}</span>}
                {pass.notes && <span className="text-gray-500">{pass.notes}</span>}
              </div>
              {mayEdit &&
                (cancelReason !== null ? (
                  <div className="space-y-2 rounded-md bg-gray-50 p-3">
                    <Field label="Why is the gate pass cancelled?" htmlFor="gp-cancel-reason" required>
                      <input id="gp-cancel-reason" type="text" maxLength={300} autoFocus value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} className={inputClass} />
                    </Field>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setCancelReason(null)} disabled={gate.isPending} className={secondaryButtonClass}>
                        Back
                      </button>
                      <button type="button" onClick={() => gate.mutate({ action: '/cancel', data: { reason: cancelReason.trim() } })} disabled={gate.isPending || cancelReason.trim().length < 3} className={dangerButtonClass}>
                        Confirm Cancelling
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-end gap-2">
                    <button type="button" onClick={() => setCancelReason('')} disabled={gate.isPending} className={secondaryButtonClass}>
                      Cancel Gate Pass
                    </button>
                    {pass.status === 'issued' && (
                      <button type="button" onClick={() => gate.mutate({ action: '/out' })} disabled={gate.isPending} className={primaryButtonClass}>
                        Mark as Out
                      </button>
                    )}
                  </div>
                ))}
            </div>
          ) : gateForm ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Vehicle number" htmlFor="gp-vehicle" required error={gateError.includes('vehicle') ? gateError : undefined}>
                  <input id="gp-vehicle" type="text" maxLength={30} value={gateForm.vehicleNumber} onChange={(event) => setGateForm({ ...gateForm, vehicleNumber: event.target.value })} className={cn(inputClass, gateError.includes('vehicle') && inputErrorClass)} />
                </Field>
                <Field label="Driver" htmlFor="gp-driver">
                  <input id="gp-driver" type="text" maxLength={100} value={gateForm.driverName} onChange={(event) => setGateForm({ ...gateForm, driverName: event.target.value })} className={inputClass} />
                </Field>
                <Field label="Driver's phone" htmlFor="gp-phone" error={gateError.includes('phone') ? gateError : undefined}>
                  <input id="gp-phone" type="tel" maxLength={20} value={gateForm.driverPhone} onChange={(event) => setGateForm({ ...gateForm, driverPhone: event.target.value })} className={cn(inputClass, gateError.includes('phone') && inputErrorClass)} />
                </Field>
                <Field label="Notes (seal number, remarks)" htmlFor="gp-notes" className="sm:col-span-3">
                  <input id="gp-notes" type="text" maxLength={300} value={gateForm.notes} onChange={(event) => setGateForm({ ...gateForm, notes: event.target.value })} className={inputClass} />
                </Field>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setGateForm(null)} disabled={gate.isPending} className={secondaryButtonClass}>
                  Back
                </button>
                <button type="button" onClick={issue} disabled={gate.isPending} className={primaryButtonClass}>
                  {gate.isPending ? 'Issuing…' : 'Confirm Gate Pass'}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{posted ? 'No gate pass issued for this challan.' : 'A cancelled challan has no gate pass.'}</p>
          )}
        </div>
      </section>

      {/* ---- printing */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={sectionTitle}>Print</span>
        {printButton('challan', 'Delivery Challan', false, 'Three copies: consignee, transporter, consigner')}
        {posted && printButton('packing-list', 'Packing List')}
        {posted && printButton('gate-pass', 'Gate Pass', !pass, pass ? undefined : 'Issue a gate pass first')}
      </div>
    </div>
  );
};

export default DispatchDocuments;
